import { CouponItemType, COUPON_CODE_TYPE, getCoupons } from './coupon'
import type { DeviceEntry as CommonDeviceEntry } from './common'
import { difference, has } from 'lodash'
import request, { ResponseData } from './request'
import { getAppDomain, getMallDomain, getContentType, getLanguae } from 'common/util'
import { getStoreArea } from 'common/store'
import { processVersionPath } from '@docs/common'
import { shoppingService } from './apiPrefix'
export type DeviceEntry = CommonDeviceEntry

export type ProductVariant = {
  id: string
  product: {
    status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED'
    title: string
    handle: string
    subtitle: {
      value: string
    }
  }
  sku: string
  availableForSale: boolean
  inventoryQuantity: number
  price: string
  image: {
    url: string
  }
  discount: { value: string }
}

export type SolutionArticle = {
  data: {
    data: {
      attributes: SolutionArticleEntry
    }
  }
  is_like: boolean
  total_like: number
}
export type SolutionArticleEntry = {
  title: string
  body: string
  share_content: string
  feedback_placeholder: string
  banner: {
    data: CMSImageEntry
  }
  enable_like: boolean
}

export type CMSImageEntry = {
  id: number
  attributes: {
    name: string
    alternativeText: string
    caption: string
    width: number
    height: number
    formats: {
      small: CMSImageFormatEntry
      thumbnail: CMSImageFormatEntry
    }
    hash: string
    ext: string
    mime: string
    size: number
    url: string
    previewUrl: null | string
    provider: string
    provider_metadata: null | string
  }
}
export type CMSImageFormatEntry = {
  ext: string
  url: string
  hash: string
  mime: string
  name: string
  path?: string | null
  size: number
  width: number
  height: number
}
export type OriginRule = {
  sku: string
  value_type: 'percentage' | 'fixed_amount'
  value: string
  rule_id: number
}
export type CouponVariant = ProductVariant & { originRule?: OriginRule; coupon: CouponItemType }

/**
 * 根据sku列表获取单品原始劵信息
 * @param skuList
 */
const getOriginRuleInfo = async (skuList: string[]) => {
  const storeArea = await getStoreArea()
  const res = await request<ResponseData<OriginRule[]>>(`${await shoppingService()}/sku/coupon`, {
    method: 'POST',
    data: {
      country: storeArea?.toUpperCase(),
      sku: skuList.filter(Boolean),
      coupon_type: 2, // 1- 品类券 2-单品券 3-运营创建的其他券
    },
  })

  return res.data
}

export const calcDiscountPriceByRule = (price: number, rule: OriginRule) => {
  const discount = Math.abs(Number(rule.value))
  return rule.value_type === 'percentage' ? price * (discount / 100) : discount
}
/**
 * 获取包含了当前可领取的原始劵信息的variants
 * @param skuList
 */
export const getVariantCouponBySKUList = async (skuList: string[], store_area: string) => {
  const [variants, coupons] = await Promise.all([
    request<
      ResponseData<{
        productVariants: {
          edges: { node: ProductVariant }[]
        }
      }>
    >(`${await getMallDomain()}/n/api/getVariantsBySKUList`, {
      method: 'POST',
      data: {
        skuList: Array.from(new Set(skuList)),
        store_area,
      },
    }).then(res => {
      if (res.code === 0 && res.data.productVariants) {
        return res.data?.productVariants?.edges
      } else {
        return []
      }
    }),
    getCoupons(1).then(res =>
      res?.filter((coupon: { code_type: COUPON_CODE_TYPE }) => coupon.code_type === COUPON_CODE_TYPE.VARIANT)
    ),
  ])

  // 当前可用的优惠券对应的sku列表
  const availableSKUList = coupons
    // 排除全场和抽奖劵
    ?.filter((coupon: { sku_or_collection_id: any }) => coupon.sku_or_collection_id)
    .reduce((res: any[], coupon: { skus: any }) => {
      res.push(...coupon.skus)
      return res
    }, [] as string[])

  let skuOriginRuleMap: Record<string, OriginRule[]> = {}
  // 用户没领过的sku列表
  const unclaimedSKUList = difference(skuList, availableSKUList)

  if (unclaimedSKUList.length) {
    // 根据没领取的sku获取原始劵
    const originRules = await getOriginRuleInfo(unclaimedSKUList)

    skuOriginRuleMap = originRules?.reduce((res: { [x: string]: any[] }, rule: { sku: string | number }) => {
      if (res[rule.sku]) {
        res[rule.sku].push(rule)
      }
      res[rule.sku] = [rule]
      return res
    }, {} as Record<string, OriginRule[]>)
  }
  const couponVariants = variants.map((variant: { node: { sku: string | number; price: any } }) => {
    return {
      ...variant.node,
      originRule: skuOriginRuleMap?.[variant.node.sku]?.sort((rule1, rule2) => {
        if (
          calcDiscountPriceByRule(Number(variant.node.price), rule1) -
          calcDiscountPriceByRule(Number(variant.node.price), rule2)
        ) {
          return -1
        }
        return 0
      })[0],
      coupon: coupons.find((coupon: { skus: (string | number)[] }) => coupon.skus.includes(variant.node.sku)),
    }
  })
  return couponVariants as CouponVariant[]
}
//获取solution列表
export const getSolutionArticle = async (id?: string) => {
  const content_type = await getContentType()
  const lang = getLanguae()
  const urlParams = new URLSearchParams(window.location.search)
  const uidFromQuery = urlParams.get('uid')
  
  // 兼容两种链接格式：
  // 1. /recommend/solution/175?uid=11111 - query 中的 uid 优先（旧格式）
  // 2. /recommend/solution/soundcore-solution-article-01?app=soundcore - 路径参数作为 uid（新格式）
  const uid = uidFromQuery || id

  // 如果是ap     使用get_solution_content_by_uid_without_login
  // 如果是sec并且有uid这个值  使用get_solution_content_by_uid_without_login
  // 其他情况使用 get_solution_content_without_login
  const link = `${await getAppDomain()}/v1/app/cms/get_solution_content_by_uid_without_login`
  const linkId = `${await getAppDomain()}/v1/app/cms/get_solution_content_without_login`

  if (
    content_type &&
    (content_type.article === 'ap-solution-articles' ||
      content_type.article === 'soundcore-solution-articles' ||
      (content_type.article === 'sec-solution-articles' && uid && uid !== 'null'))
  ) {
    const _params = {
      uid: uid,
      content_type: content_type.article,
      lang,
    }
    return request<ResponseData<SolutionArticle>>(
      link,
      {
        method: 'POST',
        data: _params,
      },
      {
        ignoreAuthToken: true,
      }
    ).then(res => {
      if (res.code === 0 && has(res, 'data.data.data.attributes')) {
        return res.data
      } else {
        return Promise.reject(null)
      }
    })
  } else {
    return request<ResponseData<SolutionArticle>>(
      linkId,
      {
        method: 'POST',
        data: {
          article_id: Number(id),
        },
      },
      {
        ignoreAuthToken: true,
      }
    ).then(res => {
      if (res.code === 0 && has(res, 'data.data.data.attributes')) {
        return res.data
      } else {
        return Promise.reject(null)
      }
    })
  }
}
/**
 * 简单领取接口
 * @param data
 * @returns
 */
export const getCouponByRuleId = async (data: {
  sku: string
  rule_id: number
  scene: string
  receive_type: number
}) => {
  return request<ResponseData<{ coupon: CouponItemType }>>(
    `${await shoppingService()}/receive/coupon/simple`,
    {
      method: 'POST',
      data,
    }
  ).then(res => res.data)
}
/**
 * 更新喜欢状态
 */
export const updateLike = async (data: { type: 'recommend' | 'solution'; article_id: number; is_like: boolean }) =>
  request<ResponseData<DeviceEntry[]>>(`${await getAppDomain()}${await processVersionPath('set_like')}`, {
    method: 'POST',
    data,
  })

/**
 *获取短链 分享
 */
export const getSortLink = async (fullLink: string) => {
  return request(`${await shoppingService()}/s/create`, {
    method: 'POST',
    data: {
      full_url: fullLink,
    },
  }).then(res => res?.data)
}
